import axios from 'axios'
import { addAxiosDateTransformer } from 'axios-date-transformer'
import { IGNORED_REFRESH_ROUTES, STATUS_CODES } from '../config/constants'
import { router } from 'src/router'
import { useAuthStore } from 'src/common/store'
import { Roles } from 'src/common/types'

export const axiosInstance = axios.create({
  baseURL: process.env.BASE_API_V2,
})

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const errorResponse = error.response
    const originalRequest = error.config

    // Throw error if issue is no response. I.e. no internet connection
    if (!errorResponse && error.code && error.code === 'ERR_NETWORK') {
      throw error
    }

    if (errorResponse?.status === STATUS_CODES.unauthorized) {
      if (
        !originalRequest._retry &&
        !IGNORED_REFRESH_ROUTES.some((route) =>
          originalRequest.url.includes(route),
        )
      ) {
        originalRequest._retry = true
        try {
          // Try a second time, pre-request interceptor will attempt token refresh if necessary
          const data = axiosInstance(originalRequest)
          return data
        } catch {
          // Fail Silently
        }
      }
      originalRequest._retry = false
      const currentRoute = router.currentRoute.value
      if (
        currentRoute.name !== 'admin.login' &&
        currentRoute.name !== 'client.login' &&
        currentRoute.name !== 'client.centerSelection'
      ) {
        const authStore = useAuthStore()
        const routeName = authStore.isClient ? 'client.login' : 'admin.login'
        await authStore.logout()
        void router.push({
          name: routeName,
        })
      }
    }

    if (errorResponse?.status === STATUS_CODES.gone) {
      await router.replace({ name: 'outdatedVersion' })
    }

    return Promise.reject(
      error instanceof Error ? error : new Error(String(error)),
    )
  },
)

export const request = addAxiosDateTransformer(axiosInstance)

axiosInstance.interceptors.request.use(async (config) => {
  let sk: string | undefined = ''

  if (config.url) {
    sk = process.env.SK_MASTER_V2
  }

  const headers = config.headers || {}
  const authStore = useAuthStore()
  const tryRefresh = !IGNORED_REFRESH_ROUTES.some((route) =>
    config.url?.includes(route),
  )
  const token = (await authStore.getAccessToken(tryRefresh)) || ''

  Object.assign(config.headers || {}, {
    ...headers,
    centerId:
      (headers as Record<string, string>).centerId ||
      authStore.currentCenterId ||
      '',
    orgId:
      (headers as Record<string, string>).orgId || authStore.currentOrgId || '',
    auth: token || '',
    client: authStore.tokenPayload?.extension_Role === Roles.Client || false,
    'Ocp-Apim-Subscription-Key': sk,
  })

  // Transform any array params values into {v1},{v2},{v3} format automatically
  if (config.params) {
    const transformed: Record<string, any> = {}
    for (const [key, value] of Object.entries(config.params)) {
      if (Array.isArray(value)) {
        // Only transform primitive arrays (string | number | boolean)
        const primitives = value.every(
          (v) =>
            ['string', 'number', 'boolean'].includes(typeof v) ||
            v instanceof String ||
            v instanceof Number ||
            v instanceof Boolean,
        )
        if (primitives) {
          transformed[key] = value.join(',')
          continue
        }
      }
      transformed[key] = value
    }
    config.params = transformed
  }

  return config
})
